import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { kApdmCampaignKindLabels } from '@euler/functions/apdm/campaignLabels';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ApdmCampaignDetailResult,
  ApdmCampaignStatus,
} from '@euler/model/apdm';
import {
  ChevronLeft,
  CirclePause,
  CirclePlay,
  CircleStop,
  LucideIcon,
  Sparkles,
} from 'lucide-react-native';
import { memo } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ApdmStatusPill, ApdmTag } from '../components/ApdmSurface';
import { apdmCampaignDates } from '../dashboard/DashboardCampaignCard';

export type CampaignControl = 'pause' | 'resume' | 'stop';

/** The controls each status allows. */
export function campaignControls(
  status: ApdmCampaignStatus,
): CampaignControl[] {
  switch (status) {
    case ApdmCampaignStatus.Reaching:
      return ['pause', 'stop'];
    case ApdmCampaignStatus.Paused:
      return ['resume', 'stop'];
    case ApdmCampaignStatus.Scheduled:
    case ApdmCampaignStatus.Reached:
      return ['stop'];
    default:
      return [];
  }
}

const kControlLook: Record<
  CampaignControl,
  { label: string; icon: LucideIcon; color: string; danger?: boolean }
> = {
  pause: { label: '暂停', icon: CirclePause, color: aiColors.amber },
  resume: { label: '继续', icon: CirclePlay, color: aiColors.green },
  stop: {
    label: '停止',
    icon: CircleStop,
    color: aiColors.destructive,
    danger: true,
  },
};

export const DetailHeader = memo(
  ({
    detail,
    sessionId,
    working,
    onBack,
    onConversation,
    onControl,
  }: {
    detail: ApdmCampaignDetailResult;
    sessionId: string | null;
    /** The control in flight, if any. */
    working: CampaignControl | null;
    onBack: () => void;
    onConversation: () => void;
    onControl: (control: CampaignControl) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const { summary } = detail;
    const controls = detail.capabilities?.manageCampaign
      ? campaignControls(summary.status)
      : [];
    const meta = [
      apdmCampaignDates(summary),
      detail.ownerName ? `负责人 ${detail.ownerName}` : null,
      '私域营销',
    ].filter(Boolean);
    const square = {
      width: rpx(40),
      height: rpx(40),
      borderRadius: rpx(10),
      alignItems: 'center',
      justifyContent: 'center',
    } as const;
    return (
      <View
        style={{
          paddingTop: insets.top + rpx(10),
          paddingBottom: rpx(14),
          paddingHorizontal: rpx(16),
          backgroundColor: aiColors.card,
          borderBottomWidth: 1,
          borderBottomColor: aiColors.border,
        }}
      >
        <View
          style={{
            width: '100%',
            maxWidth: 1120,
            alignSelf: 'center',
            gap: rpx(10),
          }}
        >
          <View
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="返回"
              onPress={onBack}
              hitSlop={8}
              style={{
                width: 32,
                height: 32,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChevronLeft size={22} color={aiColors.foreground} />
            </Pressable>
            <Typography.Text
              bold
              size={rpx(18)}
              color={aiColors.foreground}
              numberOfLines={1}
              style={{ flex: 1 }}
            >
              {summary.title}
            </Typography.Text>
          </View>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: rpx(8),
            }}
          >
            <View
              style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(6) }}
            >
              <ApdmStatusPill status={summary.status} />
              <ApdmTag
                round
                text={kApdmCampaignKindLabels[summary.kind]}
                color={aiColors.violet}
              />
            </View>
            <View style={{ flexDirection: 'row', gap: rpx(8) }}>
              {sessionId ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="打开对话"
                  onPress={onConversation}
                  style={({ pressed }) => [
                    square,
                    {
                      backgroundColor: aiColors.primary,
                      opacity: pressed ? 0.85 : 1,
                    },
                  ]}
                >
                  <Sparkles size={20} color="#fff" />
                </Pressable>
              ) : null}
              {controls.map(control => {
                const look = kControlLook[control];
                const Icon = look.icon;
                return (
                  <Pressable
                    key={control}
                    accessibilityRole="button"
                    accessibilityLabel={look.label}
                    disabled={!!working}
                    onPress={() => onControl(control)}
                    style={({ pressed }) => [
                      square,
                      {
                        borderWidth: 1,
                        borderColor: look.danger
                          ? tint(aiColors.destructive, 0.4)
                          : aiColors.border,
                        backgroundColor: aiColors.card,
                        opacity:
                          pressed || (working && working !== control) ? 0.6 : 1,
                      },
                    ]}
                  >
                    {working === control ? (
                      <ActivityIndicator size="small" color={look.color} />
                    ) : (
                      <Icon size={20} color={look.color} />
                    )}
                  </Pressable>
                );
              })}
            </View>
          </View>
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            {meta.join(' · ')}
          </Typography.Text>
        </View>
      </View>
    );
  },
);
